import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaClock, FaRedoAlt } from 'react-icons/fa';

export default function WaitingRoom() {
    const navigate = useNavigate();
    const location = useLocation();
    const [timeLeft, setTimeLeft] = useState(60); // 60 seconds countdown
    const [isRetrying, setIsRetrying] = useState(false);

    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            handleRetry();
        }
    }, [timeLeft]);

    const handleRetry = () => {
        setIsRetrying(true);
        // Navigate back to login to try again
        setTimeout(() => {
            navigate('/login');
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-950 via-indigo-900 to-purple-950 flex items-center justify-center px-4 font-sans overflow-hidden relative">
            {/* Animated background elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute top-[10%] left-[5%] w-72 h-72 bg-blue-600/20 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[10%] right-[5%] w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            <div className="max-w-md w-full bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-[2.5rem] shadow-2xl relative z-10 text-center">
                <div className="mb-8 relative flex justify-center">
                    <div className="w-24 h-24 bg-blue-600/20 rounded-full flex items-center justify-center animate-bounce">
                        <FaClock className="text-4xl text-blue-400" />
                    </div>
                </div>

                <h1 className="text-3xl font-extrabold text-white mb-4 tracking-tight">System is Busy</h1>

                <p className="text-blue-100/80 mb-8 leading-relaxed">
                    We're currently experiencing a high volume of students. To ensure a smooth experience for everyone, we've placed you in a temporary queue.
                </p>

                <div className="bg-black/20 rounded-2xl p-6 mb-8 border border-white/5">
                    <div className="text-sm font-bold text-blue-400 uppercase tracking-[0.2em] mb-2">Estimated Wait</div>
                    <div className="text-5xl font-black text-white lining-nums">
                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000 ease-linear"
                            style={{ width: `${(timeLeft / 60) * 100}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-white/40 font-medium">Auto-retrying in {timeLeft} seconds...</p>
                </div>

                <button
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="mt-8 w-full py-4 bg-white text-blue-900 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-blue-50 transition-all active:scale-95 disabled:opacity-50"
                >
                    {isRetrying ? (
                        <FaRedoAlt className="animate-spin" />
                    ) : (
                        <>
                            <FaRedoAlt />
                            Retry Now
                        </>
                    )}
                </button>

                <p className="mt-8 text-xs text-white/30">
                    Thank you for your patience. Sudesh Maths is optimized for your learning success.
                </p>
            </div>
        </div>
    );
}
